import { addWeeks, format, getISOWeek, parseISO, subDays } from 'date-fns'
import { ApiError } from '../api'
import type {
  Dag,
  Dagdeel,
  Groep,
  GroepKort,
  GroepWijziging,
  Lesmoment,
  LesmomentInvoer,
  Lesreeks,
  NieuweGroep,
  Week,
  ZetbareWeekStatus,
} from '../groep-types'
import type {
  Aanstelling,
  AanstellingInvoer,
  Medewerker,
  MedewerkerWijziging,
  NieuweMedewerker,
  Sectie,
} from '../medewerker-types'
import type { Afvinkantwoord, Melding, Upload, Voorbeeld } from '../rooster-types'
import type {
  NieuwSemester,
  Semester,
  SemesterDetail,
  SemesterWijziging,
  Sleutels,
  WeekStatus,
} from '../semester-types'
import type { NieuweTaak, Taak, TaakWijziging, TakenOverzicht } from '../taak-types'
import type {
  Niveaufase,
  Vak,
  VakCel,
  VakGroep,
  VakGroepDetail,
  VakMedewerker,
  VakRij,
  Vakmatrix,
} from '../vak-types'
import { GROEP_META, MEDEWERKERS, SEMESTERS, lesregels, overigeRegels } from './inzet-fixtures'

const STANDAARD_SLEUTELS: Sleutels = {
  urenPerDagdeel: 3,
  opslagFactor: 0.5,
  mentoraatUrenPerLesweek: 2,
  coachingUrenPerWeek: 8,
  fteUrenPerJaar: 1659,
  balansMargePercentage: 5,
}

const VAKANTIEWEKEN: Record<string, string[]> = {
  's1-2526': ['2025-10-20', '2025-12-22', '2025-12-29'],
  's2-2526': ['2026-02-16', '2026-04-27', '2026-05-04'],
  's1-2627': ['2026-10-19', '2026-12-21', '2026-12-28'],
}

const SECTIES: Sectie[] = ['nt2', 'engels', 'wiskunde', 'coaches', 'bedrijfsbureau', 'maatwerkvakken']

const SECTIE_LABEL: Record<Sectie, string> = {
  nt2: 'NT2',
  engels: 'Engels',
  wiskunde: 'Wiskunde',
  coaches: 'Coaches',
  bedrijfsbureau: 'Bedrijfsbureau',
  maatwerkvakken: 'Maatwerkvakken',
}

const DAGEN: Dag[] = ['ma', 'di', 'wo', 'do', 'vr']

const TRAJECT_LABEL: Record<'VT' | 'MT', string> = {
  VT: 'Voortraject',
  MT: 'Maatwerktraject',
}

const DAG_LABEL: Record<Dag, string> = {
  ma: 'maandag',
  di: 'dinsdag',
  wo: 'woensdag',
  do: 'donderdag',
  vr: 'vrijdag',
}

let teller = 0

function nieuwId(voorvoegsel: string): string {
  teller += 1
  return `${voorvoegsel}-${teller}`
}

function nietGevonden(bericht: string): never {
  throw new ApiError(404, { error: { code: 'niet_gevonden', message: bericht } })
}

function conflict(bericht: string): never {
  throw new ApiError(409, { error: { code: 'conflict', message: bericht } })
}

function maandagVan(datum: string): string {
  const dag = parseISO(datum)
  return format(subDays(dag, (dag.getDay() + 6) % 7), 'yyyy-MM-dd')
}

const semesters: SemesterDetail[] = SEMESTERS.map((semester) => ({
  ...semester,
  sleutels: { ...STANDAARD_SLEUTELS },
}))

function vindSemester(semesterId: string): SemesterDetail {
  const gevonden = semesters.find((semester) => semester.id === semesterId)
  if (!gevonden) nietGevonden('Onbekend semester.')
  return gevonden
}

function zonderSleutels(semester: SemesterDetail): Semester {
  return {
    id: semester.id,
    naam: semester.naam,
    jaar: semester.jaar,
    volgnummer: semester.volgnummer,
    startDatum: semester.startDatum,
    eindDatum: semester.eindDatum,
    status: semester.status,
  }
}

export function mockSemesters(): Semester[] {
  return semesters.map(zonderSleutels)
}

export function mockSemesterDetail(semesterId: string): SemesterDetail {
  return vindSemester(semesterId)
}

export function mockMaakSemester(input: NieuwSemester): SemesterDetail {
  if (input.eindDatum <= input.startDatum) {
    throw new ApiError(422, {
      error: {
        code: 'validatie_mislukt',
        message: 'Ongeldige periode.',
        details: [{ field: 'eindDatum', message: 'Moet na de startdatum liggen.' }],
      },
    })
  }

  const overlap = semesters.find(
    (semester) => input.startDatum <= semester.eindDatum && input.eindDatum >= semester.startDatum,
  )
  if (overlap) conflict(`Deze periode overlapt met "${overlap.naam}".`)

  const semester: SemesterDetail = {
    id: nieuwId('semester'),
    naam: input.naam,
    jaar: input.jaar,
    volgnummer: input.volgnummer,
    startDatum: input.startDatum,
    eindDatum: input.eindDatum,
    status: 'concept',
    sleutels: { ...STANDAARD_SLEUTELS },
  }
  semesters.unshift(semester)

  return semester
}

export function mockWijzigSemester(semesterId: string, input: SemesterWijziging): SemesterDetail {
  const semester = vindSemester(semesterId)
  if (input.startDatum !== undefined) semester.startDatum = input.startDatum
  if (input.eindDatum !== undefined) semester.eindDatum = input.eindDatum
  if (input.status !== undefined) semester.status = input.status
  return semester
}

export function mockVerwijderSemester(semesterId: string): void {
  const index = semesters.findIndex((semester) => semester.id === semesterId)
  if (index === -1) nietGevonden('Onbekend semester.')
  semesters.splice(index, 1)
  groepenPerSemester.delete(semesterId)
  takenPerSemester.delete(semesterId)
}

export function mockWijzigSleutels(semesterId: string, input: Partial<Sleutels>): Sleutels {
  const semester = vindSemester(semesterId)
  const huidig = semester.sleutels ?? { ...STANDAARD_SLEUTELS }
  const nieuw: Sleutels = { ...huidig }

  for (const sleutel of Object.keys(huidig) as Array<keyof Sleutels>) {
    const waarde = input[sleutel]
    if (typeof waarde === 'number' && waarde >= 0) nieuw[sleutel] = waarde
  }

  semester.sleutels = nieuw
  return nieuw
}

const medewerkers: Medewerker[] = MEDEWERKERS.map((bron, index) => ({
  id: bron.id,
  naam: bron.naam,
  email: bron.email,
  sectie: SECTIES[index % SECTIES.length],
  sectieLabel: SECTIE_LABEL[SECTIES[index % SECTIES.length]],
  actief: true,
  notitie: null,
  aanstellingen:
    bron.aanstellingFte === null
      ? []
      : [
          {
            id: `aanstelling-${bron.id}`,
            geldigVan: SEMESTERS[0].startDatum,
            geldigTot: null,
            brutoFte: bron.aanstellingFte,
            detacheringFte: 0,
            wtvFte: 0,
            nettoFte: bron.aanstellingFte,
            toelichting: null,
          },
        ],
}))

function vindMedewerker(medewerkerId: string): Medewerker {
  const gevonden = medewerkers.find((medewerker) => medewerker.id === medewerkerId)
  if (!gevonden) nietGevonden('Onbekende medewerker.')
  return gevonden
}

export function mockMedewerkers(alleenActief: boolean): Medewerker[] {
  return alleenActief ? medewerkers.filter((medewerker) => medewerker.actief) : medewerkers
}

export function mockMaakMedewerker(input: NieuweMedewerker): Medewerker {
  if (medewerkers.some((medewerker) => medewerker.naam === input.naam)) {
    conflict(`Er is al een medewerker met de naam "${input.naam}".`)
  }

  const sectie = input.sectie ?? null
  const medewerker: Medewerker = {
    id: nieuwId('medewerker'),
    naam: input.naam,
    email: input.email ?? null,
    sectie,
    sectieLabel: sectie ? SECTIE_LABEL[sectie] : null,
    actief: true,
    notitie: null,
    aanstellingen: [],
  }
  medewerkers.push(medewerker)

  return medewerker
}

export function mockWijzigMedewerker(medewerkerId: string, input: MedewerkerWijziging): Medewerker {
  const medewerker = vindMedewerker(medewerkerId)
  if (input.naam !== undefined) medewerker.naam = input.naam
  if (input.email !== undefined) medewerker.email = input.email
  if (input.sectie !== undefined) {
    medewerker.sectie = input.sectie
    medewerker.sectieLabel = input.sectie ? SECTIE_LABEL[input.sectie] : null
  }
  if (input.actief !== undefined) medewerker.actief = input.actief
  if (input.notitie !== undefined) medewerker.notitie = input.notitie
  return medewerker
}

function bouwAanstelling(input: AanstellingInvoer, id: string): Aanstelling {
  const detachering = input.detacheringFte ?? 0
  const wtv = input.wtvFte ?? 0
  return {
    id,
    geldigVan: input.geldigVan,
    geldigTot: input.geldigTot ?? null,
    brutoFte: input.brutoFte,
    detacheringFte: detachering,
    wtvFte: wtv,
    nettoFte: Math.max(0, input.brutoFte - detachering - wtv),
    toelichting: input.toelichting ?? null,
  }
}

export function mockMaakAanstelling(medewerkerId: string, input: AanstellingInvoer): Aanstelling {
  const medewerker = vindMedewerker(medewerkerId)
  const aanstelling = bouwAanstelling(input, nieuwId('aanstelling'))
  medewerker.aanstellingen.push(aanstelling)
  medewerker.aanstellingen.sort((a, b) => a.geldigVan.localeCompare(b.geldigVan))
  return aanstelling
}

export function mockWijzigAanstelling(
  aanstellingId: string,
  input: Partial<AanstellingInvoer>,
): Aanstelling {
  for (const medewerker of medewerkers) {
    const bestaand = medewerker.aanstellingen.find((item) => item.id === aanstellingId)
    if (!bestaand) continue

    const bijgewerkt = bouwAanstelling(
      {
        geldigVan: input.geldigVan ?? bestaand.geldigVan,
        geldigTot: input.geldigTot === undefined ? bestaand.geldigTot : input.geldigTot,
        brutoFte: input.brutoFte ?? bestaand.brutoFte,
        detacheringFte: input.detacheringFte ?? bestaand.detacheringFte,
        wtvFte: input.wtvFte ?? bestaand.wtvFte,
        toelichting: input.toelichting === undefined ? bestaand.toelichting : input.toelichting,
      },
      aanstellingId,
    )

    Object.assign(bestaand, bijgewerkt)
    return bestaand
  }

  return nietGevonden('Onbekende aanstelling.')
}

export function mockVerwijderAanstelling(aanstellingId: string): void {
  for (const medewerker of medewerkers) {
    const index = medewerker.aanstellingen.findIndex((item) => item.id === aanstellingId)
    if (index !== -1) {
      medewerker.aanstellingen.splice(index, 1)
      return
    }
  }
  nietGevonden('Onbekende aanstelling.')
}

interface MockLesreeks {
  id: string
  vak: string
  notitie: string | null
  weken: Week[]
  lesmomenten: Lesmoment[]
}

interface MockGroep {
  code: string
  traject: 'VT' | 'MT' | null
  startJaar: number | null
  startMaand: number | null
  actief: boolean
  lesreeksen: MockLesreeks[]
}

const groepenPerSemester = new Map<string, MockGroep[]>()

function bouwWeekas(semester: Semester, aantalLesweken: number): Week[] {
  const vakanties = VAKANTIEWEKEN[semester.id] ?? []
  const eerste = addWeeks(parseISO(maandagVan(semester.startDatum)), -2)
  const laatste = parseISO(semester.eindDatum)

  const weken: Week[] = []
  let cursor = eerste
  let gegeven = 0
  let inhaalGezet = false

  while (cursor <= laatste) {
    const maandag = format(cursor, 'yyyy-MM-dd')
    const inVenster = maandag >= semester.startDatum && maandag <= semester.eindDatum

    let status: WeekStatus = 'geen'
    if (inVenster && vakanties.includes(maandag)) {
      status = 'vakantie'
    } else if (inVenster && gegeven < aantalLesweken) {
      status = 'les'
      gegeven += 1
    } else if (inVenster && !inhaalGezet && gegeven >= aantalLesweken && aantalLesweken > 0) {
      status = 'inhaal'
      inhaalGezet = true
    }

    weken.push({
      maandag,
      weeknummer: getISOWeek(cursor),
      status,
      niveau: null,
      inVenster,
    })
    cursor = addWeeks(cursor, 1)
  }

  return weken
}

function bouwLesmoment(dag: Dag, dagdeel: Dagdeel, extra: Partial<Lesmoment> = {}): Lesmoment {
  return {
    id: nieuwId('lesmoment'),
    dag,
    dagLabel: DAG_LABEL[dag],
    dagVolgorde: DAGEN.indexOf(dag) + 1,
    dagdeel,
    dagdeelVolgorde: dagdeel === 'ochtend' ? 1 : 2,
    medewerkerId: null,
    medewerkerNaam: null,
    isMentor: false,
    lokaal: null,
    vanafMaandag: null,
    totMaandag: null,
    ...extra,
  }
}

function bouwGroepen(semesterId: string): MockGroep[] {
  const semester = vindSemester(semesterId)

  return Object.keys(GROEP_META).map((code, groepIndex) => {
    const meta = GROEP_META[code]
    const regels = lesregels(semesterId, code)
    const mentorId = regels.find((regel) => regel.soort === 'mentoraat')?.medewerkerId ?? null
    const lesRegels = regels.filter((regel) => regel.soort === 'les')
    const vakken = [...new Set(lesRegels.map((regel) => regel.vak ?? regel.omschrijving))]

    const lesreeksen = vakken.map((vak, vakIndex) => {
      const eigen = lesRegels.filter((regel) => (regel.vak ?? regel.omschrijving) === vak)
      const lesmomenten = eigen.map((regel, index) =>
        bouwLesmoment(
          DAGEN[(vakIndex + index) % DAGEN.length],
          (vakIndex + index) % 2 === 0 ? 'ochtend' : 'middag',
          {
            medewerkerId: regel.medewerkerId,
            medewerkerNaam:
              medewerkers.find((medewerker) => medewerker.id === regel.medewerkerId)?.naam ?? null,
            isMentor: vakIndex === 0 && index === 0 && regel.medewerkerId === mentorId,
          },
        ),
      )

      return {
        id: nieuwId('lesreeks'),
        vak,
        notitie: null,
        weken: bouwWeekas(semester, eigen[0]?.weken ?? 0),
        lesmomenten,
      }
    })

    return {
      code,
      traject: groepIndex % 2 === 0 ? 'VT' : 'MT',
      startJaar: Number(meta.startDatum.slice(0, 4)),
      startMaand: Number(meta.startDatum.slice(5, 7)),
      actief: true,
      lesreeksen,
    }
  })
}

function groepwereld(semesterId: string): MockGroep[] {
  const bestaand = groepenPerSemester.get(semesterId)
  if (bestaand) return bestaand

  const nieuw = bouwGroepen(semesterId)
  groepenPerSemester.set(semesterId, nieuw)
  return nieuw
}

function naarLesreeks(reeks: MockLesreeks): Lesreeks {
  const lesweken = reeks.weken.filter((week) => week.status === 'les')
  const gesorteerd = [...reeks.lesmomenten].sort(
    (a, b) => a.dagVolgorde - b.dagVolgorde || a.dagdeelVolgorde - b.dagdeelVolgorde,
  )

  return {
    id: reeks.id,
    vak: reeks.vak,
    notitie: reeks.notitie,
    lesweken: lesweken.length,
    leswekenInSemester: lesweken.filter((week) => week.inVenster).length,
    weken: reeks.weken,
    lesmomenten: gesorteerd,
  }
}

function naarKort(groep: MockGroep): GroepKort {
  const lesreeksen = groep.lesreeksen.map(naarLesreeks)
  const lesmomenten = groep.lesreeksen.flatMap((reeks) => reeks.lesmomenten)

  return {
    code: groep.code,
    traject: groep.traject,
    trajectLabel: groep.traject ? TRAJECT_LABEL[groep.traject] : null,
    startJaar: groep.startJaar,
    startMaand: groep.startMaand,
    actief: groep.actief,
    aantalLesreeksen: groep.lesreeksen.length,
    leswekenInSemester: lesreeksen.reduce((som, reeks) => som + reeks.leswekenInSemester, 0),
    lesmomentenZonderMedewerker: lesmomenten.filter(
      (lesmoment) => lesmoment.medewerkerId === null,
    ).length,
    heeftMentor: lesmomenten.some((lesmoment) => lesmoment.isMentor),
  }
}

function naarGroep(groep: MockGroep): Groep {
  return {
    ...naarKort(groep),
    lesreeksen: groep.lesreeksen
      .map(naarLesreeks)
      .sort((a, b) => a.vak.localeCompare(b.vak, 'nl')),
  }
}

function vindGroep(semesterId: string, code: string): MockGroep {
  const gevonden = groepwereld(semesterId).find((groep) => groep.code === code)
  if (!gevonden) nietGevonden('Onbekende groep.')
  return gevonden
}

function vindLesreeks(lesreeksId: string): { groep: MockGroep; reeks: MockLesreeks } {
  for (const groepen of groepenPerSemester.values()) {
    for (const groep of groepen) {
      const reeks = groep.lesreeksen.find((item) => item.id === lesreeksId)
      if (reeks) return { groep, reeks }
    }
  }
  return nietGevonden('Onbekende lesreeks.')
}

function vindLesmoment(lesmomentId: string): { reeks: MockLesreeks; lesmoment: Lesmoment } {
  for (const groepen of groepenPerSemester.values()) {
    for (const groep of groepen) {
      for (const reeks of groep.lesreeksen) {
        const lesmoment = reeks.lesmomenten.find((item) => item.id === lesmomentId)
        if (lesmoment) return { reeks, lesmoment }
      }
    }
  }
  return nietGevonden('Onbekend lesmoment.')
}

export function mockGroepen(semesterId: string): GroepKort[] {
  return groepwereld(semesterId).map(naarKort)
}

export function mockGroep(semesterId: string, code: string): Groep {
  return naarGroep(vindGroep(semesterId, code))
}

export function mockMaakGroep(semesterId: string, input: NieuweGroep): Groep {
  const groepen = groepwereld(semesterId)
  if (groepen.some((groep) => groep.code === input.code)) {
    conflict(`Er is al een groep met de code "${input.code}".`)
  }

  const groep: MockGroep = {
    code: input.code,
    traject: input.traject ?? null,
    startJaar: input.startJaar ?? null,
    startMaand: input.startMaand ?? null,
    actief: true,
    lesreeksen: [],
  }
  groepen.push(groep)
  groepen.sort((a, b) => a.code.localeCompare(b.code, 'nl'))

  return naarGroep(groep)
}

export function mockWijzigGroep(semesterId: string, code: string, input: GroepWijziging): Groep {
  const groep = vindGroep(semesterId, code)
  if (input.code !== undefined) groep.code = input.code
  if (input.traject !== undefined) groep.traject = input.traject
  if (input.actief !== undefined) groep.actief = input.actief
  return naarGroep(groep)
}

export function mockVerwijderGroep(code: string): void {
  for (const groepen of groepenPerSemester.values()) {
    const index = groepen.findIndex((groep) => groep.code === code)
    if (index !== -1) groepen.splice(index, 1)
  }
}

export function mockMaakLesreeks(semesterId: string, code: string, vak: string): Lesreeks {
  const groep = vindGroep(semesterId, code)
  if (groep.lesreeksen.some((reeks) => reeks.vak === vak)) {
    conflict(`Groep ${code} heeft al een reeks voor "${vak}".`)
  }

  const reeks: MockLesreeks = {
    id: nieuwId('lesreeks'),
    vak,
    notitie: null,
    weken: bouwWeekas(vindSemester(semesterId), 0),
    lesmomenten: [],
  }
  groep.lesreeksen.push(reeks)

  return naarLesreeks(reeks)
}

export function mockVerwijderLesreeks(lesreeksId: string): void {
  const { groep } = vindLesreeks(lesreeksId)
  groep.lesreeksen = groep.lesreeksen.filter((reeks) => reeks.id !== lesreeksId)
}

export function mockZetWeek(
  _semesterId: string,
  lesreeksId: string,
  maandag: string,
  status: ZetbareWeekStatus,
): Lesreeks {
  const { reeks } = vindLesreeks(lesreeksId)
  const week = reeks.weken.find((item) => item.maandag === maandag)
  if (!week) nietGevonden('Deze week hoort niet bij de reeks.')
  week.status = status

  return naarLesreeks(reeks)
}

function pasLesmomentToe(lesmoment: Lesmoment, input: Partial<LesmomentInvoer>): void {
  if (input.dag !== undefined) {
    lesmoment.dag = input.dag
    lesmoment.dagLabel = DAG_LABEL[input.dag]
    lesmoment.dagVolgorde = DAGEN.indexOf(input.dag) + 1
  }
  if (input.dagdeel !== undefined) {
    lesmoment.dagdeel = input.dagdeel
    lesmoment.dagdeelVolgorde = input.dagdeel === 'ochtend' ? 1 : 2
  }
  if (input.medewerkerId !== undefined) {
    lesmoment.medewerkerId = input.medewerkerId
    lesmoment.medewerkerNaam =
      medewerkers.find((medewerker) => medewerker.id === input.medewerkerId)?.naam ?? null
  }
  if (input.isMentor !== undefined) lesmoment.isMentor = input.isMentor
  if (input.lokaal !== undefined) lesmoment.lokaal = input.lokaal
  if (input.vanafMaandag !== undefined) lesmoment.vanafMaandag = input.vanafMaandag
  if (input.totMaandag !== undefined) lesmoment.totMaandag = input.totMaandag
}

export function mockMaakLesmoment(lesreeksId: string, input: LesmomentInvoer): Lesmoment {
  const { reeks } = vindLesreeks(lesreeksId)
  const lesmoment = bouwLesmoment(input.dag, input.dagdeel)
  pasLesmomentToe(lesmoment, input)
  if (lesmoment.isMentor) {
    for (const ander of reeks.lesmomenten) ander.isMentor = false
  }
  reeks.lesmomenten.push(lesmoment)

  return lesmoment
}

export function mockWijzigLesmoment(
  lesmomentId: string,
  input: Partial<LesmomentInvoer>,
): Lesmoment {
  const { reeks, lesmoment } = vindLesmoment(lesmomentId)
  if (input.isMentor === true) {
    for (const ander of reeks.lesmomenten) ander.isMentor = false
  }
  pasLesmomentToe(lesmoment, input)

  return lesmoment
}

export function mockVerwijderLesmoment(lesmomentId: string): void {
  const { reeks } = vindLesmoment(lesmomentId)
  reeks.lesmomenten = reeks.lesmomenten.filter((lesmoment) => lesmoment.id !== lesmomentId)
}

interface MockTakenWereld {
  toewijzingen: Map<string, Record<string, number>>
}

const taken: Taak[] = [
  ...new Set(
    MEDEWERKERS.flatMap((bron) =>
      bron.regels.filter((regel) => regel.soort === 'overig').map((regel) => regel.omschrijving),
    ),
  ),
].map((naam, index) => ({
  id: `taak-${index}`,
  naam,
  sleutelOmschrijving: null,
  volgorde: index,
  actief: true,
}))

const takenPerSemester = new Map<string, MockTakenWereld>()

function takenWereld(semesterId: string): MockTakenWereld {
  const bestaand = takenPerSemester.get(semesterId)
  if (bestaand) return bestaand

  const toewijzingen = new Map<string, Record<string, number>>()
  for (const regel of overigeRegels(semesterId)) {
    const taak = taken.find((item) => item.naam === regel.omschrijving)
    if (!taak || regel.medewerkerId === null) continue
    const rij = toewijzingen.get(taak.id) ?? {}
    rij[regel.medewerkerId] = (rij[regel.medewerkerId] ?? 0) + regel.uren
    toewijzingen.set(taak.id, rij)
  }

  const nieuw: MockTakenWereld = { toewijzingen }
  takenPerSemester.set(semesterId, nieuw)
  return nieuw
}

function naarOverzicht(semesterId: string): TakenOverzicht {
  const { toewijzingen } = takenWereld(semesterId)

  return {
    taken: taken.map((taak) => {
      const uren = toewijzingen.get(taak.id) ?? {}
      return {
        ...taak,
        uren: { ...uren },
        totaal: Object.values(uren).reduce((som, waarde) => som + waarde, 0),
      }
    }),
  }
}

export function mockTaken(semesterId: string): TakenOverzicht {
  return naarOverzicht(semesterId)
}

export function mockMaakTaak(input: NieuweTaak): Taak {
  if (taken.some((taak) => taak.naam === input.naam)) {
    conflict(`Er is al een taak met de naam "${input.naam}".`)
  }

  const taak: Taak = {
    id: nieuwId('taak'),
    naam: input.naam,
    sleutelOmschrijving: input.sleutelOmschrijving ?? null,
    volgorde: taken.length,
    actief: true,
  }
  taken.push(taak)

  return taak
}

export function mockWijzigTaak(taakId: string, input: TaakWijziging): Taak {
  const taak = taken.find((item) => item.id === taakId)
  if (!taak) nietGevonden('Onbekende taak.')
  if (input.naam !== undefined) taak.naam = input.naam
  if (input.sleutelOmschrijving !== undefined) taak.sleutelOmschrijving = input.sleutelOmschrijving
  if (input.actief !== undefined) taak.actief = input.actief
  return taak
}

export function mockVerwijderTaak(taakId: string): void {
  const index = taken.findIndex((taak) => taak.id === taakId)
  if (index === -1) nietGevonden('Onbekende taak.')
  taken.splice(index, 1)
  for (const semesterTaken of takenPerSemester.values()) semesterTaken.toewijzingen.delete(taakId)
}

export function mockZetTaakUren(
  semesterId: string,
  taakId: string,
  medewerkerId: string,
  uren: number,
): TakenOverzicht {
  const { toewijzingen } = takenWereld(semesterId)
  const rij = { ...(toewijzingen.get(taakId) ?? {}) }

  if (uren <= 0) delete rij[medewerkerId]
  else rij[medewerkerId] = uren

  toewijzingen.set(taakId, rij)

  return naarOverzicht(semesterId)
}

const uploads: Upload[] = [
  {
    id: 'upload-eerder-1',
    bestandsnaam: 'Rooster semester 2 2025-2026.xlsx',
    geuploadOp: '2026-01-19T09:24:00+01:00',
    geuploadDoor: 'w.kroon@windesheim.nl',
    aantalLesreeksen: 34,
    aantalMedewerkers: 14,
    waarschuwingen: [
      {
        cel: 'Blad1!D18',
        bericht: 'Docent "M. de Vries" staat niet in de medewerkerslijst.',
        ernst: 'let_op',
      },
    ],
  },
  {
    id: 'upload-eerder-2',
    bestandsnaam: 'Rooster semester 2 concept.xlsx',
    geuploadOp: '2026-01-12T15:02:00+01:00',
    geuploadDoor: 'w.kroon@windesheim.nl',
    aantalLesreeksen: 31,
    aantalMedewerkers: 13,
    waarschuwingen: [],
  },
  {
    id: 'upload-eerder-3',
    bestandsnaam: 'Rooster semester 2 versie 3.xlsx',
    geuploadOp: '2026-01-08T11:41:00+01:00',
    geuploadDoor: 'w.kroon@windesheim.nl',
    aantalLesreeksen: 30,
    aantalMedewerkers: 13,
    waarschuwingen: [
      { cel: 'Blad1!A22', bericht: 'Lesreeks "VT02-2605 / VOB" heeft geen weken.', ernst: 'info' },
    ],
  },
  {
    id: 'upload-eerder-4',
    bestandsnaam: 'Rooster semester 2 versie 2.xlsx',
    geuploadOp: '2025-12-19T16:18:00+01:00',
    geuploadDoor: 'j.bakker@windesheim.nl',
    aantalLesreeksen: 29,
    aantalMedewerkers: 13,
    waarschuwingen: [],
  },
  {
    id: 'upload-eerder-5',
    bestandsnaam: 'Rooster semester 2 versie 1.xlsx',
    geuploadOp: '2025-12-11T08:55:00+01:00',
    geuploadDoor: 'j.bakker@windesheim.nl',
    aantalLesreeksen: 28,
    aantalMedewerkers: 12,
    waarschuwingen: [],
  },
  {
    id: 'upload-eerder-6',
    bestandsnaam: 'Rooster semester 1 definitief.xlsx',
    geuploadOp: '2025-08-21T13:07:00+02:00',
    geuploadDoor: 'w.kroon@windesheim.nl',
    aantalLesreeksen: 33,
    aantalMedewerkers: 13,
    waarschuwingen: [
      { cel: 'Blad1!C9', bericht: 'Dagdeel "do mi" is niet herkend.', ernst: 'let_op' },
    ],
  },
  {
    id: 'upload-eerder-7',
    bestandsnaam: 'Rooster semester 1 concept.xlsx',
    geuploadOp: '2025-08-04T10:30:00+02:00',
    geuploadDoor: 'w.kroon@windesheim.nl',
    aantalLesreeksen: 32,
    aantalMedewerkers: 12,
    waarschuwingen: [],
  },
]

type RuweMelding = Omit<Melding, 'afgevinkt' | 'afgevinktOp' | 'afgevinktDoor'>

const VOORBEELDMELDINGEN: RuweMelding[] = [
  {
    sleutel: 'dagdeel_onleesbaar|Geert',
    soort: 'dagdeel_onleesbaar',
    onderwerp: 'Geert',
    bericht: 'Geen dag en dagdeel herkend in lesmoment "Geert".',
    ernst: 'let_op',
    cel: 'C22',
  },
  {
    sleutel: 'nieuwe_medewerker|Markeen',
    soort: 'nieuwe_medewerker',
    onderwerp: 'Markeen',
    bericht: 'Medewerker "Markeen" staat nog niet in de app en wordt aangemaakt.',
    ernst: 'let_op',
    cel: 'D18',
  },
  {
    sleutel: 'reeks_verdwenen|VT04-2612 / Nederlands',
    soort: 'reeks_verdwenen',
    onderwerp: 'VT04-2612 / Nederlands',
    bericht: 'Lesreeks "VT04-2612 / Nederlands" stond in de vorige upload en ontbreekt nu.',
    ernst: 'let_op',
    cel: null,
  },
  {
    sleutel: 'nieuwe_groep|VT05-2701',
    soort: 'nieuwe_groep',
    onderwerp: 'VT05-2701',
    bericht: 'Groep "VT05-2701" is nieuw en wordt aangemaakt.',
    ernst: 'let_op',
    cel: 'A31',
  },
  {
    sleutel: 'afwijkende_groepcode|MT03-2609',
    soort: 'afwijkende_groepcode',
    onderwerp: 'MT03-2609',
    bericht:
      'Weekcellen noemen groep "MT03-2608" terwijl kolom A "MT03-2609" bevat; kolom A is leidend.',
    ernst: 'let_op',
    cel: 'H23',
  },
  {
    sleutel: 'reeks_zonder_lesmomenten|VT02-2605 / VOB',
    soort: 'reeks_zonder_lesmomenten',
    onderwerp: 'VT02-2605 / VOB',
    bericht: 'Lesreeks "VT02-2605 / VOB" heeft geen lesmomenten en geen weken.',
    ernst: 'info',
    cel: 'A14',
  },
  {
    sleutel: 'onbekend_niveau|B2-C1+',
    soort: 'onbekend_niveau',
    onderwerp: 'B2-C1+',
    bericht: 'Niveau "B2-C1+" is niet bekend en blijft leeg.',
    ernst: 'info',
    cel: 'F19',
  },
]

const afvinkingen = new Map<string, Afvinkantwoord>()
const voorbeelden = new Map<string, string>()

function meldingen(): Melding[] {
  return VOORBEELDMELDINGEN.map((ruw) => {
    const afvinking = afvinkingen.get(ruw.sleutel)

    return {
      ...ruw,
      afgevinkt: afvinking !== undefined,
      afgevinktOp: afvinking?.afgevinktOp ?? null,
      afgevinktDoor: afvinking?.afgevinktDoor ?? null,
    }
  })
}

function verslag(bestandsnaam: string, opgeslagen: boolean): Voorbeeld {
  return {
    bestandsnaam,
    opgeslagen,
    groepen: 1,
    lesreeksen: 36,
    medewerkers: 1,
    vakantieweken: 0,
    wekenToegevoegd: 12,
    wekenGewijzigd: 3,
    wekenVervallen: 0,
    meldingen: meldingen(),
    semesters: semesters.map((semester) => ({ id: semester.id, naam: semester.naam })),
  }
}

export function mockUploads(): Upload[] {
  return uploads
}

export function mockVoorbeeld(bestand: File): Voorbeeld {
  const voorbeeldId = nieuwId('voorbeeld')
  voorbeelden.set(voorbeeldId, bestand.name)

  return { ...verslag(bestand.name, false), voorbeeldId }
}

export function mockBevestigVoorbeeld(voorbeeldId: string, bestandsnaam: string): Voorbeeld {
  if (!voorbeelden.has(voorbeeldId)) {
    nietGevonden('Het voorbeeld is verlopen. Kies het bestand opnieuw.')
  }
  voorbeelden.delete(voorbeeldId)

  const resultaat = verslag(bestandsnaam, true)

  uploads.unshift({
    id: nieuwId('upload'),
    bestandsnaam,
    geuploadOp: new Date().toISOString(),
    geuploadDoor: 'w.kroon@windesheim.nl',
    aantalLesreeksen: resultaat.lesreeksen,
    aantalMedewerkers: MEDEWERKERS.length,
    waarschuwingen: resultaat.meldingen
      .filter((melding) => !melding.afgevinkt)
      .map((melding) => ({
        cel: melding.cel ?? '',
        bericht: melding.bericht,
        ernst: melding.ernst,
      })),
  })

  return resultaat
}

export function mockVerwerpVoorbeeld(voorbeeldId: string): void {
  voorbeelden.delete(voorbeeldId)
}

export function mockVinkMelding(sleutel: string, bericht: string): Afvinkantwoord {
  if (!VOORBEELDMELDINGEN.some((melding) => melding.sleutel === sleutel)) {
    nietGevonden(`Onbekende melding: ${bericht}`)
  }

  const afvinking: Afvinkantwoord = {
    sleutel,
    afgevinkt: true,
    afgevinktOp: new Date().toISOString(),
    afgevinktDoor: 'w.kroon@windesheim.nl',
  }
  afvinkingen.set(sleutel, afvinking)

  return afvinking
}

export function mockOntvinkMelding(sleutel: string): void {
  afvinkingen.delete(sleutel)
}

const NIVEAUS = ['0-A1', 'A1-A2', 'A2-B1', 'B1-B2', 'B2-C1']

const NIVEAUVAKKEN = ['Lezen', 'Luisteren', 'Schrijven', 'Spreken']

interface MockVakrij {
  groep: MockGroep
  reeks: MockLesreeks
  lesweken: number
}

function lopendeReeksen(semesterId: string): MockVakrij[] {
  return groepwereld(semesterId).flatMap((groep) =>
    groep.lesreeksen
      .map((reeks) => ({
        groep,
        reeks,
        lesweken: reeks.weken.filter((week) => week.status === 'les' && week.inVenster).length,
      }))
      .filter((rij) => rij.lesweken > 0),
  )
}

function vakUren(rij: MockVakrij): number {
  const perDagdeel = STANDAARD_SLEUTELS.urenPerDagdeel * (1 + STANDAARD_SLEUTELS.opslagFactor)
  return rij.lesweken * rij.reeks.lesmomenten.length * perDagdeel
}

function vakGroep(groep: MockGroep): VakGroep {
  return {
    code: groep.code,
    traject: groep.traject,
    trajectLabel: groep.traject ? TRAJECT_LABEL[groep.traject] : null,
  }
}

/** Het mockrooster draagt geen niveaulabels, dus de taalvakken krijgen hier een fasering. */
function vakNiveaus(rij: MockVakrij): Niveaufase[] {
  if (!NIVEAUVAKKEN.includes(rij.reeks.vak)) return []

  const lesweken = rij.reeks.weken.filter((week) => week.status === 'les' && week.inVenster)
  const perFase = Math.max(4, Math.ceil(lesweken.length / 2))
  const fasen: Niveaufase[] = []

  for (let start = 0; start < lesweken.length; start += perFase) {
    const deel = lesweken.slice(start, start + perFase)
    fasen.push({
      niveau: NIVEAUS[Math.min(fasen.length, NIVEAUS.length - 1)],
      weken: deel.length,
      vanaf: deel[0].maandag,
      tot: deel[deel.length - 1].maandag,
    })
  }

  return fasen
}

function vakNamen(rijen: MockVakrij[]): string[] {
  return [...new Set(rijen.map((rij) => rij.reeks.vak))].sort((a, b) => a.localeCompare(b, 'nl'))
}

export function mockVakmatrix(semesterId: string): Vakmatrix {
  const rijen = lopendeReeksen(semesterId)

  const groepen = new Map<string, MockGroep>()
  for (const rij of rijen) groepen.set(rij.groep.code, rij.groep)

  const vakken: VakRij[] = vakNamen(rijen).map((naam) => {
    const eigen = rijen.filter((rij) => rij.reeks.vak === naam)
    const perGroep: Record<string, VakCel> = {}

    for (const rij of eigen) {
      perGroep[rij.groep.code] = {
        lesweken: rij.lesweken,
        uren: vakUren(rij),
        lesmomenten: rij.reeks.lesmomenten.length,
        zonderMedewerker: rij.reeks.lesmomenten.filter(
          (lesmoment) => lesmoment.medewerkerId === null,
        ).length,
      }
    }

    return {
      naam,
      aantalGroepen: eigen.length,
      lesweken: eigen.reduce((som, rij) => som + rij.lesweken, 0),
      uren: eigen.reduce((som, rij) => som + vakUren(rij), 0),
      medewerkers: [
        ...new Set(
          eigen.flatMap((rij) =>
            rij.reeks.lesmomenten
              .map((lesmoment) => lesmoment.medewerkerNaam)
              .filter((naam): naam is string => naam !== null),
          ),
        ),
      ].sort((a, b) => a.localeCompare(b, 'nl')),
      heeftNiveaus: eigen.some((rij) => vakNiveaus(rij).length > 0),
      perGroep,
    }
  })

  return {
    groepen: [...groepen.values()]
      .map(vakGroep)
      .sort((a, b) => a.code.localeCompare(b.code, 'nl')),
    vakken,
    totaalUren: vakken.reduce((som, vak) => som + vak.uren, 0),
  }
}

export function mockVak(semesterId: string, naam: string): Vak {
  const eigen = lopendeReeksen(semesterId)
    .filter((rij) => rij.reeks.vak === naam)
    .sort((a, b) => a.groep.code.localeCompare(b.groep.code, 'nl'))

  if (eigen.length === 0) nietGevonden(`Vak "${naam}" loopt niet in dit semester.`)

  const perMedewerker = new Map<string, VakMedewerker>()
  for (const rij of eigen) {
    const perLesmoment = vakUren(rij) / Math.max(1, rij.reeks.lesmomenten.length)

    for (const lesmoment of rij.reeks.lesmomenten) {
      if (lesmoment.medewerkerId === null || lesmoment.medewerkerNaam === null) continue

      const bestaand = perMedewerker.get(lesmoment.medewerkerId) ?? {
        medewerkerId: lesmoment.medewerkerId,
        naam: lesmoment.medewerkerNaam,
        uren: 0,
        groepen: [],
      }
      bestaand.uren += perLesmoment
      if (!bestaand.groepen.includes(rij.groep.code)) bestaand.groepen.push(rij.groep.code)
      perMedewerker.set(lesmoment.medewerkerId, bestaand)
    }
  }

  const groepen: VakGroepDetail[] = eigen.map((rij) => ({
    ...vakGroep(rij.groep),
    lesreeksId: rij.reeks.id,
    lesweken: rij.lesweken,
    uren: vakUren(rij),
    lesmomenten: [...rij.reeks.lesmomenten]
      .sort((a, b) => a.dagVolgorde - b.dagVolgorde || a.dagdeelVolgorde - b.dagdeelVolgorde)
      .map((lesmoment) => ({
        dag: lesmoment.dag,
        dagLabel: lesmoment.dagLabel,
        dagdeel: lesmoment.dagdeel,
        medewerkerNaam: lesmoment.medewerkerNaam,
        isMentor: lesmoment.isMentor,
      })),
    niveaus: vakNiveaus(rij),
  }))

  return {
    naam,
    lesweken: groepen.reduce((som, groep) => som + groep.lesweken, 0),
    uren: groepen.reduce((som, groep) => som + groep.uren, 0),
    aantalGroepen: groepen.length,
    groepen,
    medewerkers: [...perMedewerker.values()].sort((a, b) => a.naam.localeCompare(b.naam, 'nl')),
  }
}
